import React, { memo, forwardRef, useMemo, useContext } from 'react';
import { AlertIcon, CrossIcon } from '@gwc/icons';
import cn from 'classnames';
import { Text } from '../slots/Text';
import { Trails } from '../slots/Trail';
import { Typography } from '../typography';
import { Box } from '../box';
import { TextInput } from '../text-input';
import { Tag } from '../tag';
import { useColor } from '../../hooks';
import { SelectContext, Select, Clear, Toggle } from './Select';
import styles from './styles.module.css';
export const TagContext = React.createContext(null);
export const SelectTag = forwardRef(function SelectTags({ children, className, ...rest }, ref) {
    const colorClass = useColor('minor');
    const tagContext = useContext(TagContext);
    const selectContext = useContext(SelectContext);
    if (!selectContext) {
        throw new Error('Select.Tag has to be placed inside Select.Tags component');
    }
    const { state, variant } = selectContext;
    const onRemove = () => {
        if (!tagContext)
            return;
        state.selectionManager.toggleSelection(tagContext.key);
    };
    const isDisabled = useMemo(() => {
        if (!tagContext)
            return;
        return state.disabledKeys.has(tagContext.key);
    }, [state, tagContext]);
    return (React.createElement(Tag, { ...rest, className: cn(className, {
            [styles.disabled]: isDisabled,
        }), variant: variant === 'outline' && !state.isFocused ? 'outline' : 'default', ref: ref },
        children,
        !!tagContext && (React.createElement(CrossIcon, { className: cn(colorClass, styles.tagCloseIcon), onMouseDown: isDisabled ? undefined : onRemove }))));
});
export const Tags = memo(forwardRef(function Tags({ label, value, className, makeText, makeKey, tagsLimit, clearable, allowsCustomValue, ...props }, ref) {
    const tags = useMemo(() => {
        if (!Array.isArray(value))
            return [];
        const count = tagsLimit === undefined ? value.length : tagsLimit;
        return tagsLimit === undefined ? value : value.slice(0, count);
    }, [value, tagsLimit]);
    return (React.createElement(Select, { "aria-label": typeof label === 'string' ? label : undefined, ...props, value: value, ref: ref, animated: true, className: cn(styles.tagsSelect, className), makeKey: makeKey, makeText: makeText, multi: true }, ({ labelProps, inputProps, error }) => {
        var _a;
        return (React.createElement(React.Fragment, null,
            React.createElement(Text, null,
                !!label && !Boolean(tags.length) && (React.createElement(Typography, { ...labelProps, mb: "xs", className: cn(labelProps.className, styles.tagsLabel) }, label)),
                React.createElement(Box, { className: styles.tagsList },
                    tags.map((x, i) => (React.createElement("div", { className: styles.tag, key: makeKey(x) },
                        React.createElement(TagContext.Provider, { value: { key: makeKey(x) } },
                            React.createElement(SelectTag, null, makeText(x)))))),
                    tags.length !== (value === null || value === void 0 ? void 0 : value.length) && (React.createElement("div", { className: styles.tag },
                        React.createElement(SelectTag, null, `+${((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) - tags.length}`))),
                    React.createElement(TextInput, { ...inputProps, className: cn(styles.input, allowsCustomValue === false ? styles.zeroInput : styles.tagInput) }))),
            clearable !== false && React.createElement(Clear, null),
            error ? (React.createElement(Trails.Icon, { color: "error" },
                React.createElement(AlertIcon, null))) : (React.createElement(Toggle, null))));
    }));
}));
